/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useMutation } from '@tanstack/react-query';

import { EnableMaintenanceResponseSchema } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type EnableMaintenanceModeMutationConfig = {
  onSuccess?: (data: EnableMaintenanceResponseSchema, variables: EnableMaintenanceModeParams) => void;
  onError?: (error: Error, variables: EnableMaintenanceModeParams) => void;
  onSettled?: (
    data: EnableMaintenanceResponseSchema | undefined,
    error: Error | null,
    variables: EnableMaintenanceModeParams,
  ) => void;
  onMutate?: (variables: EnableMaintenanceModeParams) => void;
};

export type EnableMaintenanceModeParams = {
  nodeId: string;
};

/**
 * Hook for enabling maintenance mode on a node.
 *
 * Returns the maintenance config ID without assigning it to the node.
 * Use the assign endpoint to apply the config.
 *
 * API Endpoint:
 * - PUT /tenants/:tenantId/nodes/:id/maintenance - Enable maintenance mode
 *
 * @param config.onSuccess - Called after successful maintenance mode enable
 * @param config.onError - Called if enable fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 * @param config.onMutate - Called before mutation starts
 *
 * @returns React Query mutation result for enabling maintenance mode
 */
export function useEnableMaintenanceModeMutation({
  onSuccess,
  onError,
  onSettled,
  onMutate,
}: EnableMaintenanceModeMutationConfig) {
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const { mutateAsync, isPending, isError, error } = useMutation<
    EnableMaintenanceResponseSchema,
    Error,
    EnableMaintenanceModeParams
  >({
    mutationFn: async ({ nodeId }: EnableMaintenanceModeParams) => {
      return mgmtPlaneAPIClient.nodes.enableMaintenanceMode({
        tenantId: tenantId!,
        id: nodeId,
      });
    },
    onSuccess,
    onError,
    onSettled,
    onMutate,
  });

  return { mutateAsync, isPending, isError, error };
}
